<template>
	<div class="profile-page">
		<main class="main-content" ref="mainContent">
			<header class="page-header">
				<h1>{{ $t('tab.profile') }}</h1>
			</header>

			<section v-if="!isLoggedIn" class="login-card">
				<div class="login-icon" aria-hidden="true">◎</div>
				<h2 class="login-title">{{ $t('profile.loginTitle') }}</h2>
				<p class="login-desc">{{ $t('profile.loginDesc') }}</p>
				<button class="primary-button" type="button" @click="onLogin">
					{{ $t('profile.loginBtn') }}
				</button>
			</section>

			<template v-else>
				<section class="user-card">
					<div class="avatar">
						<img :src="userInfo.avatar || defaultAvatar" alt="" @error="handleAvatarError" />
					</div>
					<div class="user-info">
						<div class="user-name">
							{{ userInfo.nickname || $t('profile.guest') }}
							<span v-if="isMember" class="vip-label">VIP</span>
						</div>
						<div class="user-id">{{ $t('profile.userId') }}: {{ userInfo.id || '-' }}</div>
						<div v-if="isMember && userInfo.dtime" class="member-expiry">
							VIP · {{ formatDate(userInfo.dtime) }}
						</div>
					</div>
				</section>

				<section class="balance-card" role="button" @click="onRecharge">
					<div>
						<span class="eyebrow">{{ $t('profile.pointsBalance') }}</span>
						<strong class="balance-value">{{ formatPoints(userInfo.money) }}</strong>
					</div>
					<div class="balance-action">
						{{ $t('profile.balance') }}
						<span class="chevron">›</span>
					</div>
				</section>

				<section class="subscription-card" role="button" @click="onSubscription">
					<div class="subscription-mark">VIP</div>
					<div class="subscription-copy">
						<strong>{{ $t('profile.subscriptionTitle') }}</strong>
						<span>{{ $t('profile.subscriptionDesc') }}</span>
					</div>
					<button class="subscription-button" type="button">
						{{ $t('profile.subscriptionBtn') }}
					</button>
				</section>

				<section class="menu-list">
					<button
						v-for="item in menuItems"
						:key="item.id"
						class="menu-item"
						type="button"
						@click="onMenuClick(item)"
					>
						<span :class="['menu-icon', `menu-icon-${item.id}`]" aria-hidden="true">
							<svg v-if="item.id === 'favorite'" viewBox="0 0 24 24">
								<path d="M12 20.5S4 16 4 9.5A4.5 4.5 0 0 1 12 6.7a4.5 4.5 0 0 1 8 2.8c0 6.5-8 11-8 11Z" />
							</svg>
							<svg v-else-if="item.id === 'history'" viewBox="0 0 24 24">
								<path d="M4.5 8A8.5 8.5 0 1 1 3.8 15" />
								<path d="M4.5 3v5h5M12 7.5V12l3 2" />
							</svg>
							<svg v-else-if="item.id === 'recharge'" viewBox="0 0 24 24">
								<rect x="3" y="5" width="18" height="14" rx="3" />
								<path d="M3 9.5h18M7 15h4" />
							</svg>
							<svg v-else-if="item.id === 'settings'" viewBox="0 0 24 24">
								<path d="M12 15.2A3.2 3.2 0 1 0 12 8.8a3.2 3.2 0 0 0 0 6.4Z" />
								<path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a1.9 1.9 0 0 1-2.7 2.7l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a1.9 1.9 0 0 1-3.8 0v-.2a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1A1.9 1.9 0 0 1 4.8 17l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3.5a1.9 1.9 0 0 1 0-3.8h.2a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1A1.9 1.9 0 0 1 7.5 4.8l.1.1a1.6 1.6 0 0 0 1.8.3h.1a1.6 1.6 0 0 0 .9-1.5V3.5a1.9 1.9 0 0 1 3.8 0v.2a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1A1.9 1.9 0 0 1 19.8 7.5l-.1.1a1.6 1.6 0 0 0-.3 1.8v.1a1.6 1.6 0 0 0 1.5.9h.1a1.9 1.9 0 0 1 0 3.8h-.2a1.6 1.6 0 0 0-1.4.8Z" />
							</svg>
							<svg v-else viewBox="0 0 24 24">
								<path d="M6.8 4.5h3l1.5 4-2 1.8a15 15 0 0 0 4.4 4.4l1.8-2 4 1.5v3a2 2 0 0 1-2 2C10.6 18.6 5.4 13.4 4.8 6.5a2 2 0 0 1 2-2Z" />
							</svg>
						</span>
						<span class="menu-content">{{ item.labelKey ? $t(item.labelKey) : $t(`profile.menu.${item.key}`) }}</span>
						<span class="chevron">›</span>
					</button>
				</section>
			</template>

			<footer class="legal-links">
				<button type="button" @click="showTerms">{{ $t('legal.terms') }}</button>
				<span></span>
				<button type="button" @click="showPrivacy">{{ $t('legal.policy') }}</button>
			</footer>
		</main>

		<!-- 底部导航 -->
		<tabBar currentNav="profile" />
	</div>
</template>

<script>
import tabBar from '@/components/tabBar.vue';
import {userRInfo} from "@/apis/user"
export default {
	name:"UserMember",
	components: {
		tabBar
	},
	data() {
		return {
			isLoggedIn: false,
			defaultAvatar: require('@/static/video/davatar.png'),
			userInfo: {
				id: 1,
				username: 'ドラマファン',
				name: 'ドラマファン',
				email: 'drama@fan.jp',
				avatar: '',
				money: 0,
				group_id: 0
			},
			userPoints: 3250,
			menuItems: [
				{ id: 'favorite', key: 'favorite' },
				{ id: 'history', key: 'history' },
				{ id: 'recharge', key: 'rechargeHistory' },
				{ id: 'contact', key: 'contact' },
				{ id: 'settings', labelKey: 'settings.title' }
			]
		};
	},
	computed: {
		isMember() {
			return Number(this.userInfo.group_id) === 2
		}
	},
	mounted() {
		// this.checkLoginStatus();
	},
	// 当使用 keep-alive 时，每次激活重新检查登录状态
	activated() {
		this.checkLoginStatus();
	},
	methods: {
		async checkLoginStatus() {
			const token = localStorage.getItem('token')

			if (!token) {
				this.isLoggedIn = false
				this.$router.push('/base/login')
				return
			}

			this.isLoggedIn = true
			await this.getUserInfo()
		},
		async getUserInfo() {
			const userInfo = this.$getUserInfo()

			if (!userInfo || !userInfo.id) {
				return
			}

			try {
				const { data } = await userRInfo({
					uid: userInfo.id
				})
				if (data) {
					this.userInfo = data
					this.userPoints = Number(data.money || 0)
					this.$setUserInfo(data)
				}
			} catch (err) {
				localStorage.removeItem('token')
				this.$removeUserInfo()
				this.isLoggedIn = false
				this.$router.push('/base/login')
				console.error('获取用户信息失败', err)
			}
		},

		formatPoints(points) {
			const value = Number(points || 0)
			return value.toLocaleString('ja-JP')
		},

		formatDate(timestamp) {
			const value = Number(timestamp || 0)
			if (!value) return '-'

			const date = new Date(value * 1000)
			const year = date.getFullYear()
			const month = String(date.getMonth() + 1).padStart(2, '0')
			const day = String(date.getDate()).padStart(2, '0')
			return `${year}-${month}-${day}`
		},

		handleAvatarError() {
			this.userInfo.avatar = this.defaultAvatar
		},

		onLogin() {
			this.silentLogin()
		},

		onRecharge() {
			this.$router.push('/user/recharge/recharge')
		},

		onSubscription() {
			this.$router.push('/user/subscription/subscription')
		},

		onMenuClick(item) {
			switch (item.id) {
				case 'favorite':
					this.$router.push('/user/favorite/favorite')
					break
				case 'history':
					this.$router.push('/user/history/history')
					break
				case 'recharge':
					this.$router.push('/user/order/order')
					break
				case 'contact':
					this.$router.push('/user/contact/contact')
					break
				case 'settings':
					this.$router.push('/settings/settings')
					break
				default:
					break
			}
		},

		onLogout() {
			const confirmLogout = confirm('ログアウトしてもよろしいですか？')
			if (!confirmLogout) return

			localStorage.removeItem('userinfo')
			localStorage.removeItem('token')
			localStorage.removeItem('account')

			this.isLoggedIn = false

			alert('ログアウトしました')
		},

		showTerms() {
			this.$router.push('/legal/terms')
		},

		showPrivacy() {
			this.$router.push('/legal/privacy')
		}
	}
};
</script>

<style scoped>
.profile-page {
	width: 100%;
	max-width: 450px;
	min-height: 100vh;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	background: #f5f5f7;
	color: #1d1d1f;
	font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif;
}

.main-content {
	flex: 1;
	padding: 0 16px 100px;
	overflow-y: auto;
}

.page-header {
	padding: 28px 4px 18px;
}

.page-header h1 {
	margin: 0;
	font-size: 32px;
	font-weight: 700;
	line-height: 1.1;
	letter-spacing: -0.04em;
}

.login-card {
	margin-top: 8px;
	padding: 34px 22px 24px;
	border-radius: 24px;
	background: #fff;
	text-align: center;
	box-shadow: 0 2px 14px rgba(0, 0, 0, 0.04);
}

.login-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 64px;
	height: 64px;
	margin: 0 auto 18px;
	border-radius: 50%;
	background: #f2f2f7;
	color: #0071e3;
	font-size: 38px;
	font-weight: 300;
}

.login-title {
	margin: 0 0 7px;
	font-size: 21px;
	font-weight: 700;
	letter-spacing: -0.025em;
}

.login-desc {
	margin: 0 0 24px;
	color: #86868b;
	font-size: 13px;
	line-height: 1.5;
}

.primary-button {
	width: 100%;
	height: 50px;
	border: 0;
	border-radius: 14px;
	background: #0071e3;
	color: #fff;
	font: inherit;
	font-size: 15px;
	font-weight: 600;
	box-shadow: 0 5px 14px rgba(0, 113, 227, 0.2);
}

.primary-button:active {
	background: #0068d1;
	transform: scale(0.99);
}

.user-card {
	display: flex;
	align-items: center;
	gap: 15px;
	padding: 18px;
	border-radius: 20px;
	background: #fff;
	box-shadow: 0 2px 14px rgba(0, 0, 0, 0.04);
}

.avatar {
	flex: 0 0 auto;
	width: 58px;
	height: 58px;
	border-radius: 50%;
	overflow: hidden;
	background: #e5e5ea;
}

.avatar img {
	width: 100%;
	height: 100%;
	display: block;
	object-fit: cover;
}

.user-info {
	flex: 1;
	min-width: 0;
}

.user-name {
	display: flex;
	align-items: center;
	gap: 7px;
	overflow: hidden;
	font-size: 18px;
	font-weight: 650;
	line-height: 1.3;
	letter-spacing: -0.02em;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.user-id {
	margin-top: 5px;
	color: #86868b;
	font-size: 12px;
}

.member-expiry {
	margin-top: 5px;
	color: #0071e3;
	font-size: 11px;
	font-weight: 500;
}

.vip-label {
	padding: 3px 6px;
	border-radius: 6px;
	background: #1d1d1f;
	color: #fff;
	font-size: 9px;
	font-weight: 700;
	letter-spacing: 0.06em;
}

.balance-card {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-top: 12px;
	padding: 18px;
	border-radius: 20px;
	background: #fff;
	box-shadow: 0 2px 14px rgba(0, 0, 0, 0.04);
}

.balance-card:active,
.subscription-card:active,
.menu-item:active {
	transform: scale(0.99);
}

.eyebrow {
	display: block;
	margin-bottom: 4px;
	color: #86868b;
	font-size: 11px;
}

.balance-value {
	display: block;
	font-size: 27px;
	font-weight: 700;
	line-height: 1.1;
	letter-spacing: -0.04em;
}

.balance-action {
	display: flex;
	align-items: center;
	gap: 6px;
	color: #0071e3;
	font-size: 12px;
	font-weight: 500;
}

.subscription-card {
	display: flex;
	align-items: center;
	gap: 13px;
	margin-top: 12px;
	padding: 16px;
	border-radius: 20px;
	background: linear-gradient(145deg, #29292b, #111113);
	color: #fff;
	box-shadow: 0 8px 22px rgba(0, 0, 0, 0.16);
}

.subscription-mark {
	display: flex;
	flex: 0 0 auto;
	align-items: center;
	justify-content: center;
	width: 42px;
	height: 42px;
	border-radius: 12px;
	background: #fff;
	color: #1d1d1f;
	font-size: 11px;
	font-weight: 800;
	letter-spacing: 0.06em;
}

.subscription-copy {
	display: flex;
	flex: 1;
	min-width: 0;
	flex-direction: column;
}

.subscription-copy strong {
	font-size: 14px;
	font-weight: 600;
}

.subscription-copy span {
	margin-top: 4px;
	overflow: hidden;
	color: #a1a1a6;
	font-size: 11px;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.subscription-button {
	flex: 0 0 auto;
	height: 32px;
	padding: 0 12px;
	border: 0;
	border-radius: 999px;
	background: #fff;
	color: #1d1d1f;
	font: inherit;
	font-size: 11px;
	font-weight: 600;
}

.menu-list {
	margin-top: 20px;
	overflow: hidden;
	border-radius: 20px;
	background: #fff;
	box-shadow: 0 2px 14px rgba(0, 0, 0, 0.04);
}

.menu-item {
	display: flex;
	align-items: center;
	width: 100%;
	min-height: 57px;
	padding: 0 16px;
	border: 0;
	border-bottom: 1px solid #e5e5ea;
	background: transparent;
	color: #1d1d1f;
	font: inherit;
	text-align: left;
}

.menu-item:last-child {
	border-bottom: 0;
}

.menu-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 auto;
	width: 30px;
	height: 30px;
	margin-right: 12px;
	border-radius: 8px;
	color: #fff;
	box-shadow: inset 0 0 0 0.5px rgba(255, 255, 255, 0.25);
}

.menu-icon svg {
	display: block;
	width: 18px;
	height: 18px;
	fill: none;
	stroke: currentColor;
	stroke-width: 1.8;
	stroke-linecap: round;
	stroke-linejoin: round;
}

.menu-icon-favorite {
	background: #ff375f;
}

.menu-icon-history {
	background: #5856d6;
}

.menu-icon-recharge {
	background: #34c759;
}

.menu-icon-contact {
	background: #007aff;
}

.menu-icon-settings {
	background: #8e8e93;
}

.menu-content {
	flex: 1;
	font-size: 14px;
	font-weight: 500;
}

.chevron {
	color: #c7c7cc;
	font-size: 22px;
	font-weight: 300;
}

.legal-links {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 12px;
	padding: 28px 0 8px;
}

.legal-links span {
	width: 3px;
	height: 3px;
	border-radius: 50%;
	background: #c7c7cc;
}

.legal-links button {
	padding: 4px;
	border: 0;
	background: transparent;
	color: #86868b;
	font: inherit;
	font-size: 11px;
}
</style>
